import { useTRPC } from '@/utils/trpc';
import {
  collectAoeReferences,
  getHealthGorillaRequisitionId,
  getServiceRequestTestName,
  HEALTH_GORILLA_REQUISITION_SYSTEM,
} from '@liza/fhir-utils';
import type { ServiceRequest } from '@medplum/fhirtypes';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { type AoeGroup, LabOrderDetail } from '@liza/ui/components/records';
import { getObservationIdentifier } from '@liza/ui/utils/observation';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDiagnosticReportObservations } from './useDiagnosticReportObservations';

type Props = {
  id: string;
  medplumPatientId: string;
  /** Navigate to a lab's detail (the Labs view) when a result row is clicked. */
  onSelectLab?: (labIdentifier: string) => void;
};

/**
 * Resolve the AOE QuestionnaireResponse refs carried by a lab order family (parent SR + its
 * child SRs) and fetch each QR. Returns `aoeGroups: undefined` while still discovering refs,
 * `aoeGroups: []` once we know the family has no QRs, and the resolved groups once every QR
 * is loaded — the section component uses these states to gate its render.
 */
function useLabOrderAoeGroups(
  labOrder: ServiceRequest | undefined,
  isLabOrderLoading: boolean,
  childServiceRequests: ServiceRequest[] | undefined,
  isChildSearchLoading: boolean,
): { aoeGroups: AoeGroup[] | undefined; isLoading: boolean } {
  const trpc = useTRPC();
  const aoeRefs = labOrder ? collectAoeReferences(labOrder, childServiceRequests ?? []) : [];

  const aoeQueries = useQueries({
    queries: aoeRefs.map((ref) =>
      trpc.patient.questionnaireResponse.get.queryOptions({ id: ref.questionnaireResponseId }),
    ),
  });

  const isDiscovering = isLabOrderLoading || (!!labOrder?.id && isChildSearchLoading);
  const isLoading = isDiscovering || aoeQueries.some((q) => q.isLoading);

  const aoeGroups: AoeGroup[] | undefined = (() => {
    if (isDiscovering) return undefined;
    if (aoeRefs.length === 0) return [];
    if (aoeQueries.some((q) => !q.data)) return undefined;
    return aoeRefs.map((ref, i) => ({
      testName: getServiceRequestTestName(ref.sourceServiceRequest),
      questionnaireResponse: aoeQueries[i]!.data!.questionnaireResponse,
    }));
  })();

  return { aoeGroups, isLoading };
}

export const LabOrder = ({ id, medplumPatientId, onSelectLab }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();

  const labOrderQuery = useQuery(trpc.patient.serviceRequest.get.queryOptions({ id }));
  const labOrder = labOrderQuery.data?.serviceRequest;

  const requisitionId = labOrder ? getHealthGorillaRequisitionId(labOrder) : undefined;

  // Requisition documents — `LabOrderRequisition` DocumentReferences keyed to the HG requisition id.
  const requisitionDocsQuery = useQuery(
    trpc.patient.documentReference.list.queryOptions(
      {
        medplumPatientId,
        options: {
          searchParams: {
            category: 'LabOrderRequisition',
            identifier: requisitionId ? `${HEALTH_GORILLA_REQUISITION_SYSTEM}|${requisitionId}` : undefined,
          },
        },
      },
      { enabled: !!requisitionId },
    ),
  );

  // Specimen label PDFs — same identifier pivot, different category.
  const specimenLabelDocsQuery = useQuery(
    trpc.patient.documentReference.list.queryOptions(
      {
        medplumPatientId,
        options: {
          searchParams: {
            category: 'SpecimenLabel',
            identifier: requisitionId ? `${HEALTH_GORILLA_REQUISITION_SYSTEM}|${requisitionId}` : undefined,
          },
        },
      },
      { enabled: !!requisitionId },
    ),
  );

  // Child SRs — HG splits an order into per-test children that hold the AOE QRs in their
  // own `supportingInfo[]`. We need them all so we can collect every QR ref the family carries.
  const childServiceRequestsQuery = useQuery(
    trpc.patient.serviceRequest.list.queryOptions(
      {
        medplumPatientId,
        options: {
          searchParams: { 'based-on': labOrder?.id ? `ServiceRequest/${labOrder.id}` : undefined },
        },
      },
      { enabled: !!labOrder?.id },
    ),
  );

  const { aoeGroups, isLoading: isLoadingAoeGroups } = useLabOrderAoeGroups(
    labOrder,
    labOrderQuery.isLoading,
    childServiceRequestsQuery.data?.serviceRequests,
    childServiceRequestsQuery.isLoading,
  );

  // DiagnosticReport — match the Medplum sample's "most recent linked DR" approach. HG can
  // emit multiple DRs in theory (panel results split, amendments) but our test data shows
  // one per order; multi-DR support is future work. Omit `status` so the query returns
  // reports in any status: `preliminary` (lab has results, awaiting verification) and
  // `final` (verified) are both meaningful here.
  const diagnosticReportListQuery = useQuery(
    trpc.patient.diagnosticReport.list.queryOptions(
      {
        medplumPatientId,
        options: {
          searchParams: {
            'based-on': labOrder?.id ? `ServiceRequest/${labOrder.id}` : undefined,
            _sort: '-_lastUpdated',
            _count: 1,
          },
        },
      },
      { enabled: !!labOrder?.id },
    ),
  );
  const diagnosticReport = diagnosticReportListQuery.data?.diagnosticReports[0];

  // Recursively fetch every Observation referenced from `result[]` and `hasMember[]`. The
  // hook owns the cumulative state needed to converge — see its docstring for details. It
  // takes a list, so the single report goes in as an array of one; memoized to keep the
  // reference stable across renders.
  const diagnosticReports = useMemo(() => (diagnosticReport ? [diagnosticReport] : []), [diagnosticReport]);
  const {
    loadedObservations,
    isLoading: isLoadingObservations,
    hasError: hasDiagnosticReportError,
  } = useDiagnosticReportObservations(diagnosticReports);

  const isDiscoveringDr = labOrderQuery.isLoading || (!!labOrder?.id && diagnosticReportListQuery.isLoading);
  const isLoadingDiagnosticReport = isDiscoveringDr || (!!diagnosticReport && isLoadingObservations);

  return (
    <QueryStateHandler query={labOrderQuery} emptyMessage={t('labOrderNotFound')}>
      {(data) => (
        <LabOrderDetail
          serviceRequest={data.serviceRequest}
          requisitionDocuments={requisitionDocsQuery.data?.documentReferences}
          isLoadingRequisitionDocuments={!!requisitionId && requisitionDocsQuery.isLoading}
          specimenLabelDocuments={specimenLabelDocsQuery.data?.documentReferences}
          isLoadingSpecimenLabelDocuments={!!requisitionId && specimenLabelDocsQuery.isLoading}
          aoeGroups={aoeGroups}
          isLoadingAoeGroups={isLoadingAoeGroups}
          diagnosticReport={diagnosticReport}
          diagnosticReportObservations={loadedObservations}
          isLoadingDiagnosticReport={isLoadingDiagnosticReport}
          hasDiagnosticReportError={hasDiagnosticReportError}
          onSelectResultObservation={
            onSelectLab ? (observation): void => onSelectLab(getObservationIdentifier(observation)) : undefined
          }
        />
      )}
    </QueryStateHandler>
  );
};
